var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MulticastDelegate } from "flavr-common";
import { peerService } from "../multiplayer/peer/PeerService";
import { Validator } from "jsonschema";
const validator = new Validator();
const LOCAL_STORE_KEY = "_FlaVR_preferences_store";
const SCHEMA = {
    id: "/Store",
    preferences: {
        type: "object",
        properties: {
            microphone: { type: "string", default: "on" }, // Warum sind die nicht boolean?
            speakers: { type: "string", default: "on" }, // Warum sind die nicht boolean?
            playGongSounds: { type: "string", default: "on" }, // Warum sind die nicht boolean?
            gongVolume: { type: "number", default: 1 },
            voiceEchoCancellation: { type: "string", default: "exact" },
            voiceNoiseSuppression: { type: "string", default: "exact" },
            voiceAutoGainControl: { type: "string", default: "exact" },
            voiceActivityDetection: { type: "boolean", default: false },
            audioCodec: { type: "string", default: "" },
            audioBandwidth: { type: "string", default: "" },
            audioSendBitRate: { type: "string", default: "" },
            audioRecvBitRate: { type: "string", default: "" },
            audioPriority: { type: "string", default: "low" },
            audioNetworkPriority: { type: "string", default: "low" },
            audioMaxFramerate: { type: "string", default: "" },
            opusStereo: { type: "boolean", default: false },
            opusFec: { type: "boolean", default: true },
            opusDtx: { type: "boolean", default: true },
            opusMaxPlaybackRate: { type: "string", default: "" },
            opusMaxAverageBitrate: { type: "string", default: "" },
            opusPtime: { type: "string", default: "" },
            adaptivePtime: { type: "boolean", default: false },
            videoGoogleStartBitrate: { type: "string", default: "" },
            videoGoogleMaxBitrate: { type: "string", default: "" },
            videoGoogleMinBitrate: { type: "string", default: "" },
            avatarAudioDistanceModel: { type: "string", default: "linear" },
            avatarAudioRolloffFactor: { type: "number", default: 1 },
            avatarAudioRefDistance: { type: "number", default: 10 },
            avatarAudioMaxDistance: { type: "number", default: 100 },
            disablePositionalAudio: { type: "boolean", default: false },
            mouseSensitivity: { type: "number", default: 0.2 },
            movementSensitivity: { type: "number", default: 1.5 },
            movementSensitivityVR: { type: "number", default: 1 },
            analogStickDeadZoneThreshold: { type: "number", default: 0.1 },
            disableGrabMovement: { type: "boolean", default: false },
            disableGrabRotation: { type: "boolean", default: false },
            disableAnalogStickMovement: { type: "boolean", default: false },
            activateToolViewerBelt: { type: "boolean", default: true },
            raycastEnabled: { type: "boolean", default: true },
            raycastVisualizationEnabled: { type: "boolean", default: true },
            microphoneDeviceId: { type: "string", default: "default" },
            microphoneDefaultEnabled: { type: "boolean", default: true },
            speakersDeviceId: { type: "string", default: "default" },
            speakersDefaultEnabled: { type: "boolean", default: true },
            cameraDeviceId: { type: "string", default: "default" },
            cameraDefaultEnabled: { type: "boolean", default: true },
            useWebcamBlur: { type: "boolean", default: false },
            debug: { type: "boolean", default: false },
            htmlRenderer: { type: "string", default: "html2Canvas" },
            materialQuality: { type: "string", default: "" },
            useDynamicLights: { type: "string", default: "on" },
            shadowMapType: { type: "string", default: "1" },
            ambientLightingBrightness: { type: "number", default: 0.8 },
            autoResolution: { type: "string", default: "on" },
            framerateThresholdLow: { type: "number", default: 30 },
            framerateThresholdHigh: { type: "number", default: 50 },
            changeRate: { type: "number", default: 0.1 },
            measuringTime: { type: "number", default: 1.5 },
            resolutionFactor: { type: "number", default: 1 },
            showAvatarVRStyle: { type: "boolean", default: false },
            displayLanguage: { type: "string", default: "" },
            streaming: { type: "string", default: "meta" },
            autoStartVRLiveView: { type: "boolean", default: false },
            vrMovementMode: { type: "string", default: "walk+teleport" }
        },
        required: ["materialQuality"]
    }
};
class PreferencesService {
    constructor() {
        this.store = {
            preferences: {}
        };
        // ToDo. maybe another name
        this.memoryPreferences = new Map();
        this.onPreferenceChanged = new Set();
        this.onStoreChanged = new MulticastDelegate();
        this.shouldPromptForRefresh = false;
        if (localStorage.getItem(LOCAL_STORE_KEY) === null) {
            localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify({}));
        }
        // When storage is updated in another window
        window.addEventListener("storage", (e) => {
            if (e.key !== LOCAL_STORE_KEY)
                return;
            this.load();
            this.onStoreChanged.invoke();
        });
        this.load();
        this.onPreferenceChanged.add((key, newValue) => __awaiter(this, void 0, void 0, function* () {
            switch (key) {
                case "audioBandwidth":
                case "audioPriority":
                case "audioNetworkPriority":
                    if (peerService.peerMgr) {
                        const audioBandwidth = this.getPreferredValue("audioBandwidth", "");
                        const audioBandwidthValue = !audioBandwidth || audioBandwidth === "unlimited" ? undefined : Number(audioBandwidth);
                        const audioPriority = this.getPreferredValue("audioPriority", "");
                        const audioPriorityValue = audioPriority || undefined;
                        const audioNetworkPriority = this.getPreferredValue("audioNetworkPriority", "");
                        const audioNetworkPriorityValue = audioNetworkPriority || undefined;
                        for (const peer of peerService.peerMgr.peers.values()) {
                            yield peer.mediaConnections.updateBandwidthRestriction(audioBandwidthValue, audioPriorityValue, audioNetworkPriorityValue, "audio");
                        }
                    }
                    break;
                case "audioMaxFramerate":
                    if (peerService.peerMgr) {
                        for (const peer of peerService.peerMgr.peers.values()) {
                            yield peer.mediaConnections.setRtpEncodingParametersByKind("audio", {
                                maxFramerate: this.valueToNumber(newValue, undefined)
                            });
                        }
                    }
                    break;
                case "opusPtime":
                    if (peerService.peerMgr) {
                        for (const peer of peerService.peerMgr.peers.values()) {
                            yield peer.mediaConnections.setRtpEncodingParametersByKind("audio", {
                                ptime: this.valueToNumber(newValue, undefined)
                            });
                        }
                    }
                    break;
                case "adaptivePtime":
                    if (peerService.peerMgr) {
                        for (const peer of peerService.peerMgr.peers.values()) {
                            yield peer.mediaConnections.setRtpEncodingParametersByKind("audio", {
                                adaptivePtime: !!newValue
                            });
                        }
                    }
                    break;
                case "disablePositionalAudio":
                case "autoStartVRLiveView":
                    break;
                default:
                    return false;
            }
            return true;
        }));
    }
    get schema() {
        return SCHEMA;
    }
    setMemoryPreference(key, value) {
        if (!SCHEMA.preferences.properties.hasOwnProperty(key)) {
            console.warn("setMemoryPreferences: ignore preference", key);
            return;
        }
        this.memoryPreferences.set(key, value);
    }
    updatePreferences(preferences, shouldBeStored) {
        const result = validator.validate({ preferences }, SCHEMA);
        if (!result.valid) {
            for (const error of result.errors) {
                delete error.instance[error.argument];
            }
        }
        for (const preference of preferences) {
            if (!preference.key)
                continue;
            if (!this.store.preferences.hasOwnProperty(preference.key)) {
                console.warn("updatePreferences: preference can't be changed", preference.key);
                continue;
            }
            const newValue = preference.value;
            const oldValue = this.store.preferences[preference.key];
            // only update memory if key is set
            if (this.memoryPreferences.has(preference.key)) {
                this.setMemoryPreference(preference.key, newValue);
            }
            this.store.preferences[preference.key] = newValue;
            if (oldValue === newValue)
                continue;
            const results = [];
            for (const callback of this.onPreferenceChanged) {
                results.push(callback(preference.key, newValue, oldValue));
            }
            Promise.all(results)
                .then((results) => {
                // If at least one key is not handled, tell user to refresh the page:
                if (results.includes(false)) {
                    this.shouldPromptForRefresh = true;
                }
            })
                .catch((error) => {
                console.error(error);
            })
                .finally(() => {
                // Wait for all promises to finish, otherwise this.shouldPromptForRefresh might not be up to date
                this.onStoreChanged.invoke();
            });
        }
        if (shouldBeStored === undefined || shouldBeStored)
            localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(this.store));
    }
    getPreferredValue(key, defaultValue) {
        if (this.memoryPreferences.has(key)) {
            return this.memoryPreferences.get(key);
        }
        if (!(key in this.store.preferences))
            return defaultValue;
        return this.store.preferences[key];
    }
    getNumberPreferredValue(key, defaultValue) {
        const value = this.getPreferredValue(key, defaultValue);
        return this.valueToNumber(value, defaultValue);
    }
    getBooleanPreferredValue(key, defaultValue) {
        const value = this.getPreferredValue(key, defaultValue);
        if (value === undefined)
            return defaultValue;
        if (typeof value === "boolean") {
            return value;
        }
        let result;
        if (value === "" && defaultValue !== undefined)
            result = defaultValue;
        else
            result = !!value;
        return result;
    }
    valueToNumber(value, defaultValue) {
        if (value === undefined)
            return defaultValue;
        if (typeof value === "string") {
            if (value === "")
                return defaultValue;
            value = Number(value);
            if (isNaN(value))
                return defaultValue;
        }
        if (typeof value !== "number")
            return defaultValue;
        return value;
    }
    load() {
        var _a;
        try {
            this.store = JSON.parse((_a = localStorage.getItem(LOCAL_STORE_KEY)) !== null && _a !== void 0 ? _a : "{}");
            if (this.store && this.store.preferences) {
                let hasChanged = false;
                for (const [key, value] of Object.entries(SCHEMA.preferences.properties)) {
                    if (!this.store.preferences.hasOwnProperty(key)) {
                        this.store.preferences[key] = value.default;
                        hasChanged = true;
                    }
                }
                if (hasChanged) {
                    localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(this.store));
                }
            }
        }
        catch (error) {
            console.error(error);
            this.store.preferences = {};
        }
        if (!this.store.preferences || Object.keys(this.store.preferences).length === 0) {
            this.setDefault();
        }
    }
    setDefault() {
        if (!this.store) {
            this.store = {
                preferences: {}
            };
        }
        this.store.preferences = {};
        for (const [key, value] of Object.entries(SCHEMA.preferences.properties)) {
            this.store.preferences[key] = value.default;
        }
        localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(this.store));
    }
}
export const preferencesService = new PreferencesService();
